import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, FileText, TrendingUp, DollarSign, Search, 
  Eye, CheckCircle, Clock, AlertCircle, Plus, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../lib/auth';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showCreditItemDialog, setShowCreditItemDialog] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [clientsRes, statsRes] = await Promise.all([
        adminAPI.getClients(),
        adminAPI.getStats(),
      ]);
      setClients(clientsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  const loadClientDetail = async (clientId) => {
    try {
      const res = await adminAPI.getClientDetail(clientId);
      setClientDetail(res.data);
      setSelectedClient(clientId);
    } catch (error) {
      toast.error('Failed to load client details');
    }
  };

  const updateClientStatus = async (clientId, status) => {
    try {
      await adminAPI.updateClientStatus(clientId, status);
      toast.success('Client status updated');
      loadData();
      if (selectedClient === clientId) {
        loadClientDetail(clientId);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-badge status-pending',
      active: 'status-badge status-active',
      completed: 'status-badge status-completed',
      cancelled: 'status-badge status-cancelled',
      inactive: 'status-badge status-pending',
      disputed: 'status-badge status-pending',
      removed: 'status-badge status-completed',
      verified: 'status-badge status-active',
    };
    return <span className={styles[status] || 'status-badge'}>{status}</span>;
  };

  const filteredClients = clients.filter(client => 
    client.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout showFooter={false}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="min-h-screen bg-slate-50 py-8" data-testid="admin-dashboard">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-slate-900">
              Admin Dashboard
            </h1>
            <p className="text-slate-600 mt-1">
              Manage clients and track Credit Restoration progress
            </p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.total_clients}</p>
                      <p className="text-xs text-slate-600">Total Clients</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.active_clients}</p>
                      <p className="text-xs text-slate-600">Active</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.pending_clients}</p>
                      <p className="text-xs text-slate-600">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats.total_payments}</p>
                      <p className="text-xs text-slate-600">Payments</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Client List */}
            <div className="lg:col-span-1">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Clients</CardTitle>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search clients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                      data-testid="search-clients"
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[600px] overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => loadClientDetail(client.id)}
                        className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                          selectedClient === client.id ? 'bg-slate-100' : ''
                        }`}
                        data-testid={`client-row-${client.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-slate-900">{client.full_name}</p>
                            <p className="text-sm text-slate-600">{client.email}</p>
                          </div>
                          {getStatusBadge(client.status)}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Joined: {new Date(client.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Client Detail */}
            <div className="lg:col-span-2">
              {clientDetail ? (
                <Card className="border-slate-200">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{clientDetail.client.full_name}</CardTitle>
                        <p className="text-slate-600">{clientDetail.client.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={clientDetail.client.status}
                          onValueChange={(value) => updateClientStatus(clientDetail.client.id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="info" className="space-y-4">
                      <TabsList>
                        <TabsTrigger value="info">Info</TabsTrigger>
                        <TabsTrigger value="documents">Documents ({clientDetail.documents.length})</TabsTrigger>
                        <TabsTrigger value="updates">Updates ({clientDetail.updates.length})</TabsTrigger>
                        <TabsTrigger value="credit-items">Credit Items ({clientDetail.credit_items.length})</TabsTrigger>
                      </TabsList>

                      <TabsContent value="info">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">Phone</p>
                            <p className="font-medium">{clientDetail.client.phone || 'Not provided'}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">Date of Birth</p>
                            <p className="font-medium">{clientDetail.client.date_of_birth || 'Not provided'}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">Subscription</p>
                            {getStatusBadge(clientDetail.client.subscription_status)}
                          </div>
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">SSN (Last 4)</p>
                            <p className="font-medium font-mono">{clientDetail.client.ssn_last_four || '****'}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-lg col-span-2">
                            <p className="text-xs text-slate-500 mb-1">Address</p>
                            <p className="font-medium">{clientDetail.client.address || 'Not provided'}</p>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="documents">
                        {clientDetail.documents.length === 0 ? (
                          <p className="text-center text-slate-500 py-8">No documents uploaded</p>
                        ) : (
                          <div className="space-y-2">
                            {clientDetail.documents.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <FileText className="h-5 w-5 text-slate-400" />
                                  <div>
                                    <p className="font-medium capitalize">{doc.document_type.replace('_', ' ')}</p>
                                    <p className="text-xs text-slate-500">{doc.filename}</p>
                                  </div>
                                </div>
                                <p className="text-xs text-slate-500">
                                  {new Date(doc.uploaded_at).toLocaleDateString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="updates">
                        <div className="flex justify-end mb-4">
                          <AddUpdateDialog 
                            clientId={clientDetail.client.id} 
                            onSuccess={() => loadClientDetail(clientDetail.client.id)}
                          />
                        </div>
                        {clientDetail.updates.length === 0 ? (
                          <p className="text-center text-slate-500 py-8">No updates yet</p>
                        ) : (
                          <div className="space-y-4">
                            {clientDetail.updates.map((update) => (
                              <div key={update.id} className="p-4 bg-slate-50 rounded-lg">
                                <div className="flex justify-between items-start mb-2">
                                  <h4 className="font-semibold">{update.title}</h4>
                                  <span className="text-xs text-slate-500">
                                    {new Date(update.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-600">{update.description}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="credit-items">
                        <div className="flex justify-end mb-4">
                          <AddCreditItemDialog 
                            clientId={clientDetail.client.id} 
                            onSuccess={() => loadClientDetail(clientDetail.client.id)}
                          />
                        </div>
                        {clientDetail.credit_items.length === 0 ? (
                          <p className="text-center text-slate-500 py-8">No credit items</p>
                        ) : (
                          <div className="space-y-2">
                            {clientDetail.credit_items.map((item) => (
                              <div key={item.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{item.creditor_name}</p>
                                    {getStatusBadge(item.status)}
                                  </div>
                                  <p className="text-sm text-slate-600 capitalize">{item.item_type.replace('_', ' ')}</p>
                                  {item.amount && <p className="text-sm text-slate-500">${item.amount}</p>}
                                </div>
                                <Select
                                  value={item.status}
                                  onValueChange={async (value) => {
                                    await adminAPI.updateCreditItem(item.id, value);
                                    loadClientDetail(clientDetail.client.id);
                                  }}
                                >
                                  <SelectTrigger className="w-28">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="disputed">Disputed</SelectItem>
                                    <SelectItem value="removed">Removed</SelectItem>
                                    <SelectItem value="verified">Verified</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">Select a client to view details</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// Add Update Dialog Component
function AddUpdateDialog({ clientId, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    update_type: '35_day_update',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.createClientUpdate(clientId, formData);
      toast.success('Update created');
      setOpen(false);
      setFormData({ title: '', description: '', update_type: '35_day_update' });
      onSuccess();
    } catch (error) {
      toast.error('Failed to create update');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add Update
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Progress Update</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Update Type</Label>
            <Select
              value={formData.update_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, update_type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="35_day_update">35-Day Update</SelectItem>
                <SelectItem value="action_required">Action Required</SelectItem>
                <SelectItem value="status_change">Status Change</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Update title"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Update description"
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Creating...' : 'Create Update'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Add Credit Item Dialog Component
function AddCreditItemDialog({ clientId, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    creditor_name: '',
    item_type: 'collection',
    amount: '',
    dispute_reason: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminAPI.createCreditItem(clientId, {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
      });
      toast.success('Credit item added');
      setOpen(false);
      setFormData({ creditor_name: '', item_type: 'collection', amount: '', dispute_reason: '' });
      onSuccess();
    } catch (error) {
      toast.error('Failed to add credit item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add Credit Item
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Credit Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Item Type</Label>
            <Select
              value={formData.item_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, item_type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="charge_off">Charge-Off</SelectItem>
                <SelectItem value="collection">Collection</SelectItem>
                <SelectItem value="inquiry">Inquiry</SelectItem>
                <SelectItem value="public_record">Public Record</SelectItem>
                <SelectItem value="personal_info">Personal Info</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Creditor Name</Label>
            <Input
              value={formData.creditor_name}
              onChange={(e) => setFormData(prev => ({ ...prev, creditor_name: e.target.value }))}
              placeholder="Creditor name"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Amount (optional)</Label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
            />
          </div>
          <div className="space-y-2">
            <Label>Dispute Reason</Label>
            <Textarea
              value={formData.dispute_reason}
              onChange={(e) => setFormData(prev => ({ ...prev, dispute_reason: e.target.value }))}
              placeholder="Reason for dispute"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Adding...' : 'Add Item'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
